import { Component, Vue } from 'vue-property-decorator';
import Toast from '@common/helpers/toast';
import {
  KarteDraft,
  KarteDraftForm,
  KarteRequestToken,
  extractKarteDraftForm,
  formatKarteDraftSavedAt,
  isStaleKarteRequest,
} from '@common/helpers/karte-draft';
import {
  KarteDraftNewerKarte,
  fetchKarteDraftApi,
  saveKarteDraftApi,
} from '@common/services/karte-draft';
import { fetchReservationAnswersApi } from '@common/services/reservation-answers';

/**
 * カルテ入力中の一時保存 (No.173) — shared by the karte popup on the patient
 * detail screen (v2) and the one on the chat screen (messages), which are two
 * copies of the same form and would otherwise drift apart. Anything both
 * popups fetch or gate on at open (the draft, the BMI answers, the loading
 * state) belongs here rather than in either popup.
 *
 * What it does NOT do: decide when to save. The popups own that, because
 * "the doctor pressed 一時保存" and "the doctor pressed 保存" are theirs to
 * tell apart.
 */
@Component
export default class KarteDraftMixin extends Vue {
  protected isSavingKarteDraft: boolean = false;
  protected isLoadingKarteDraft: boolean = false;

  /** Server timestamp of the stored draft; '' when there is none. */
  protected karteDraftSavedAt: string = '';

  /**
   * No.173 Q5 (案C): set when another doctor saved a karte for this 診療 after
   * this draft was written. The draft is untouched; the popup shows a banner
   * so the doctor re-reads before continuing.
   */
  protected karteDraftNewerKarte: KarteDraftNewerKarte | null = null;

  /** No.173 Q7 — 問診 answers for the BMI line, loaded on every opening. */
  protected reservationAnswers: any[] = [];
  protected isLoadingReservationAnswers: boolean = false;

  /**
   * The form as it stood at the last 一時保存 (or at restore). Compared against
   * the live form to warn on reload — without it, closing the tab after a save
   * would nag about changes that are already on the server.
   */
  private karteDraftBaseline: string = '';

  /**
   * Incremented every time the popup opens (beginKarteOpen). Every request
   * records the value it started under; a response carrying an older value
   * belongs to a previous opening and is dropped — see isStaleKarteRequest.
   */
  private karteOpenGeneration: number = 0;

  created() {
    window.addEventListener('beforeunload', this.warnUnsavedKarteDraft);
  }

  beforeDestroy() {
    window.removeEventListener('beforeunload', this.warnUnsavedKarteDraft);
  }

  /**
   * True until everything the popup fetches on open has arrived — the draft
   * and the 問診 answers. Both popups gate their overlay, the comment box and
   * both save buttons on it: typing into the template, or pressing 一時保存,
   * before the draft lands would lose the draft's content.
   */
  protected get isKarteLoading(): boolean {
    return this.isLoadingKarteDraft || this.isLoadingReservationAnswers;
  }

  protected get karteDraftSavedAtLabel(): string {
    return formatKarteDraftSavedAt(this.karteDraftSavedAt || null);
  }

  /** Banner text for Q5 案C, or '' when there is nothing to warn about. */
  protected get karteDraftNewerKarteLabel(): string {
    if (!this.karteDraftNewerKarte) {
      return '';
    }
    const doctor = this.karteDraftNewerKarte.doctor_name || '他の医師';
    const at = formatKarteDraftSavedAt(this.karteDraftNewerKarte.created_at);
    const when = at ? `（${at}）` : '';
    return `この一時保存のあとに、${doctor}がカルテを保存しました${when}。内容をご確認ください。`;
  }

  /**
   * Loads the caller's own draft for this 診療 and hands it back, or null.
   *
   * A failure is deliberately silent: the doctor asked to open the karte, not
   * to load a draft, and an error toast over an empty popup would read as
   * "the karte is broken". The draft stays on the server for the next open.
   */
  protected async loadKarteDraft(
    patientId: number | null | undefined,
    userTreatmentId: number | null | undefined,
  ): Promise<KarteDraft | null> {
    if (!patientId || !userTreatmentId) {
      return null;
    }
    const token = this.karteRequestToken(patientId, userTreatmentId);
    this.isLoadingKarteDraft = true;
    try {
      const { draft, newerKarte } = await fetchKarteDraftApi(patientId, userTreatmentId);
      // The popup may have been re-opened for another patient while this was
      // in flight; this answer is theirs, not the one on screen.
      if (this.isKarteRequestStale(token)) {
        return null;
      }
      this.karteDraftSavedAt = (draft && draft.updated_at) || '';
      this.karteDraftNewerKarte = newerKarte;
      return draft;
    } catch (error) {
      return null;
    } finally {
      // A stale request must not clear the flag a newer one is still holding.
      if (!this.isKarteRequestStale(token)) {
        this.isLoadingKarteDraft = false;
      }
    }
  }

  /**
   * Records the form as "nothing unsaved yet". Called by the popup once it has
   * finished opening, because a new karte is prefilled with DEFAULT_KARTE_
   * TEMPLATE — without this the reload warning would fire on a popup the
   * doctor has not typed a character into.
   */
  protected markKarteDraftBaseline(form: any): void {
    this.karteDraftBaseline = this.snapshot(extractKarteDraftForm(form));
  }

  /**
   * Writes the popup's current form as the draft. An empty form clears it
   * server-side, which is what 一時保存 on an untouched popup should do.
   *
   * Returns true when the write landed, so the caller can decide whether to
   * close the popup — closing after a failed save is how the text gets lost.
   */
  protected async persistKarteDraft(
    patientId: number | null | undefined,
    userTreatmentId: number | null | undefined,
    form: any,
  ): Promise<boolean> {
    if (!patientId || !userTreatmentId || this.isSavingKarteDraft) {
      return false;
    }
    const draftForm: KarteDraftForm = extractKarteDraftForm(form);
    const token = this.karteRequestToken(patientId, userTreatmentId);
    this.isSavingKarteDraft = true;
    try {
      const saved = await saveKarteDraftApi(patientId, userTreatmentId, draftForm);
      if (this.isKarteRequestStale(token)) {
        // The save landed — for the patient it was made for. The popup has
        // since moved on, so report success but leave what it now shows alone:
        // stamping the new patient with the old one's save time is exactly the
        // cross-patient leak the guard exists to stop.
        Toast.success('一時保存しました');
        return true;
      }
      this.karteDraftSavedAt = (saved && saved.updated_at) || '';
      this.karteDraftBaseline = this.snapshot(draftForm);
      // No.173 Q5-3 (案B): an empty form is stored as an empty draft, not
      // deleted, so there is one outcome and one message.
      Toast.success('一時保存しました');
      // The doctor has now written on top of whatever they were warned about,
      // so the banner has served its purpose.
      this.karteDraftNewerKarte = null;
      return true;
    } catch (error) {
      // No annotation on the catch variable: the project is on TypeScript
      // 3.9, and catch-clause type annotations arrived in 4.0 (TS1196). The
      // variable is implicitly `any` here, which is what the helper expects.
      Toast.error(this.karteDraftErrorMessage(error));
      return false;
    } finally {
      this.isSavingKarteDraft = false;
    }
  }

  /**
   * Loads the 問診 answers behind the BMI line for whatever the popup shows
   * now (karteDraftContext), under the same stale-response guard as the draft.
   *
   * Lives here, not in each popup, for the reason the mixin exists: the two
   * popups had identical copies of this, and a fix to one would have left the
   * other behind. Reading the ids from karteDraftContext also means the ids
   * the request uses and the ids the guard checks can no longer disagree.
   */
  protected async loadReservationAnswers(): Promise<void> {
    const { patientId, treatmentId } = this.karteDraftContext();
    this.reservationAnswers = [];
    this.isLoadingReservationAnswers = true;
    const token = this.karteRequestToken(patientId, treatmentId);
    let answers: any[] = [];
    try {
      answers = await fetchReservationAnswersApi(patientId, treatmentId);
    } catch (error) {
      // The service already swallows API errors; this keeps the overlay from
      // locking the form if that ever changes.
    }
    // A late answer would show another patient's BMI. It also leaves the
    // loading flag alone — a newer opening owns it now.
    if (this.isKarteRequestStale(token)) {
      return;
    }
    this.reservationAnswers = answers;
    this.isLoadingReservationAnswers = false;
  }

  /**
   * Called by the popup at the start of every opening. Invalidates every
   * request started for an earlier opening, whichever patient it was for.
   */
  protected beginKarteOpen(): void {
    this.karteOpenGeneration += 1;
  }

  /** Records which opening, patient and treatment a request was made for. */
  protected karteRequestToken(patientId: any, treatmentId: any): KarteRequestToken {
    return { generation: this.karteOpenGeneration, patientId, treatmentId };
  }

  /** True when a response must be dropped rather than applied. */
  protected isKarteRequestStale(token: KarteRequestToken): boolean {
    return isStaleKarteRequest(token, this.karteOpenGeneration, this.karteDraftContext());
  }

  /**
   * Overridden by each popup: the patient and treatment it is showing right
   * now. The two popups resolve these differently (store vs. prop), so the
   * mixin asks rather than assumes.
   */
  protected karteDraftContext(): { patientId: any; treatmentId: any } {
    return { patientId: null, treatmentId: null };
  }

  /**
   * Called once the real karte is saved — the server has already dropped the
   * draft row inside that transaction, so this only clears what the popup
   * shows.
   */
  protected resetKarteDraftState(): void {
    this.karteDraftSavedAt = '';
    this.karteDraftBaseline = '';
    this.karteDraftNewerKarte = null;
    // The popup shows a loading overlay off this flag. A load the stale guard
    // dropped never clears it (its `finally` must not touch a newer load's
    // flag), so without this reset, re-opening onto a saved karte — which
    // loads no draft — would leave the overlay up for good.
    this.isLoadingKarteDraft = false;
  }

  /**
   * True when the live form differs from the last thing written to the server.
   * The popups override the getter's input by keeping `prescriptionForm`;
   * `karteDraftLiveForm` is what each one exposes.
   */
  protected get hasUnsavedKarteDraft(): boolean {
    const live = this.karteDraftLiveForm();
    if (!live) {
      return false;
    }
    return this.snapshot(extractKarteDraftForm(live)) !== this.karteDraftBaseline;
  }

  /**
   * Overridden by each popup to return its own form object. The default keeps
   * the mixin usable (and the warning silent) in a component that has not
   * opted in.
   */
  protected karteDraftLiveForm(): any {
    return null;
  }

  /**
   * Same shape as reloadHandler in views/settings/posts/create.vue:
   * addEventListener rather than assigning window.onbeforeunload, because both
   * karte popups can be mounted at once and an assignment would let the last
   * one clobber the other.
   */
  private warnUnsavedKarteDraft(event: BeforeUnloadEvent): void {
    if (!this.hasUnsavedKarteDraft) {
      return;
    }
    event.preventDefault();
    event.returnValue = '';
  }

  private snapshot(form: KarteDraftForm | KarteDraft): string {
    const draftForm = extractKarteDraftForm(form);
    return JSON.stringify({
      content: (draftForm.content || '').trim(),
      type: draftForm.type || null,
      // Plan order is a UI artefact, not a difference the doctor made.
      plans: [...draftForm.plans].map((plan: any) => String(plan?.id ?? plan)).sort(),
    });
  }

  /**
   * Toast.error only understands a string, an object carrying `message`, or an
   * array; an axios failure is an Error and matches none of them, so it would
   * be dropped silently.
   */
  private karteDraftErrorMessage(error: any): string {
    return error?.response?.data?.message || '一時保存に失敗しました。';
  }
}
